import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowSquareOut, CheckCircle, FilePdf, Copy, DownloadSimple } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { RENDERERS, AGENT_LABELS } from "./blueprintRenderers";
import { webinarBlueprintToMarkdown, downloadMarkdown } from "../lib/blueprintToMarkdown";
import { runPdfExportJob, downloadBlobAsFile } from "../lib/pdfExportJob";
import { toast } from "sonner";

/**
 * Full blueprint preview modal on cream paper surface.
 * Renders the saved blueprint without leaving the Asset Vault.
 */
export default function BlueprintPreviewModal({ open, agentType, item = null, onClose }) {
  const [blueprint, setBlueprint] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const pdfRef = useRef(null);
  const navigate = useNavigate();

  const handleExportPdf = async () => {
    if (!blueprint) {
      toast.error("Nincs blueprint");
      return;
    }
    // Az appban látható modal-riportban scrape-eljük az NYITOTT
    // V2Collapsible-ket — a print render ezeket pontosan ki fogja nyitni.
    const openIds = [];
    try {
      document.querySelectorAll('button[data-testid^="v2-collapsible-"]').forEach((b) => {
        const arrow = b.querySelector('span[style*="rotate"]');
        if (arrow && (arrow.style.transform || "").includes("180")) {
          openIds.push(b.getAttribute("data-testid"));
        }
      });
    } catch {}

    setExporting(true);
    try {
      // Async job flow — indítunk egy background job-ot, pollolunk,
      // majd letöltjük a PDF blob-ot. Ingress timeout-mentes.
      const blob = await runPdfExportJob({
        start: () =>
          api.post("/export/pdf/blueprint/start", {
            blueprint_type: agentType,
            entry_id: item?.id || undefined,
            open_sections: openIds.length ? openIds.join(",") : undefined,
          }),
      });
      downloadBlobAsFile(blob, `${item?.name || agentType}.pdf`);
      toast.success("PDF letöltve");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Ismeretlen hiba";
      toast.error("PDF generálás sikertelen: " + detail);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (!open || !agentType) return;
    const meta = AGENT_LABELS[agentType];
    setLoading(true);
    setBlueprint(null);
    // Ha az `item` objektum inline blueprint-tel érkezik (pl. Tudástár
    // listáról kapott saved item), akkor NEM kell külön fetch — a
    // saved snapshot már ott van. Ez a Webinár / poszt / minden olyan
    // típusnál működik, amit a `/asset-vault` listing eleve inline
    // ad vissza. Így NEM ragad be végtelen „Betöltés..." állapotban.
    if (item && item.blueprint) {
      setBlueprint(item.blueprint || null);
      setSavedAt(item.saved_at || item.updated_at || null);
      setLoading(false);
      return;
    }
    // Fallback: fetch a Foundation endpoint-ról (voice / client /
    // offer) — ott a live blueprint jön, nem a saved snapshot.
    if (!meta || !meta.endpoint) {
      // Ismeretlen típus és nincs inline blueprint — ne ragadjon be.
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await api.get(meta.endpoint);
        setBlueprint(res.data?.blueprint || null);
        setSavedAt(res.data?.saved_at || res.data?.updated_at || null);
      } catch {
        setBlueprint(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, agentType, item]);

  const meta = AGENT_LABELS[agentType] || { name: item?.name || agentType, kind: null };
  const render = RENDERERS[agentType];
  const isWebinar = meta.kind === "webinar";

  // Webinár-specifikus Markdown export (title + saved date + szekciók +
  // slide-onkénti headline / bullet / kérdés / CTA / vizuális javaslat /
  // előadói jegyzet). A `meta.name` a magyar címke ("Webinár – ..."), a
  // felhasználó által mentett név viszont az `item.name` — azt preferáljuk.
  const buildWebinarMarkdown = () =>
    webinarBlueprintToMarkdown(
      blueprint,
      item?.name || meta.name || "Webinár",
      savedAt,
    );

  const handleCopyMarkdown = async () => {
    if (!blueprint) {
      toast.error("Nincs betöltött webinár");
      return;
    }
    try {
      const md = buildWebinarMarkdown();
      await navigator.clipboard.writeText(md);
      toast.success("Vágólapra másolva");
    } catch {
      toast.error("Másolás sikertelen");
    }
  };

  const handleDownloadMarkdown = () => {
    if (!blueprint) {
      toast.error("Nincs betöltött webinár");
      return;
    }
    try {
      const md = buildWebinarMarkdown();
      const filename = (item?.name || meta.name || "webinar").trim() || "webinar";
      downloadMarkdown(filename, md);
      toast.success("Markdown letöltve");
    } catch {
      toast.error("Letöltés sikertelen");
    }
  };

  const handleOpenAgent = () => {
    // Stabilizált Megnyitás: az `AGENT_LABELS[agentType].route` a
    // canonical wizard útvonal (pl. minden webinar sub-variant →
    // '/agents/webinar'). Webinároknál a mentett `item` állapotot is
    // átadjuk `state.savedItem`-en, hogy a WebinarAgent a Section
    // Carousel-t read-only viewer módban tudja rendereli — újragenerálás
    // nélkül, a mentett `carousel_content`-ből visszatöltve.
    //
    // F3 varázslóknál (voice / client / offer) a mentett snapshot ID-t
    // `?saved_id=` query-paraméteren adjuk át — az `AgentWorkflow`
    // felismeri, GET-eli a `/asset-vault/{id}` végpontról, majd az output
    // nézetben rendereli ÚJ generálás nélkül.
    const target = meta.route || (agentType ? `/agents/${agentType}` : null);
    if (!target) {
      toast.error("Ez a típus nem nyitható meg a varázslóban");
      return;
    }
    onClose();
    if (isWebinar && item) {
      navigate(target, { state: { savedItem: item } });
    } else if ((agentType === "voice" || agentType === "client" || agentType === "offer") && item?.id) {
      navigate(`${target}?saved_id=${encodeURIComponent(item.id)}`);
    } else {
      navigate(target);
    }
  };

  // Magyaros footer szöveg — típus szerint. Ha ismeretlen típus, egy
  // generikus szép „mentett előnézet" üzenet jön (soha nem a régi
  // belső „a blueprint mentett verziója" fordulat).
  const KIND_LABELS = {
    webinar: "A mentett webinár előnézete.",
    post: "A mentett Facebook poszt előnézete.",
    email: "A mentett email előnézete.",
    "lead-magnet": "A mentett ügyfélmágnes előnézete.",
    voice: "A mentett márkahang előnézete.",
    client: "A mentett avatar előnézete.",
    offer: "A mentett ajánlat előnézete.",
  };
  const footerLabel = KIND_LABELS[meta.kind || agentType] || "A mentett anyag előnézete.";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-6"
          style={{
            background: "rgba(5, 5, 10, 0.72)",
            backdropFilter: "blur(8px)",
          }}
          onClick={onClose}
          data-testid="blueprint-preview-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ type: "spring", damping: 22, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl max-h-[90vh] rounded-3xl overflow-hidden flex flex-col"
            style={{
              background: "#f3eee4",
              boxShadow:
                "0 40px 120px rgba(0,0,0,0.5), inset 1px 0 0 rgba(255,255,255,0.5)",
            }}
            data-preview-theme="cream"
          >
            {/* Ambient purple glow */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute"
              style={{
                top: -40,
                right: -40,
                width: 220,
                height: 220,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(124,108,242,0.10), transparent 70%)",
              }}
            />

            {/* Header */}
            <div className="relative flex items-start justify-between px-8 pt-7 pb-5 border-b border-[rgba(10,10,18,0.06)]">
              <div className="min-w-0 pr-4">
                <div className="mex-eyebrow mb-1">Tudástár · {meta.name}</div>
                <h2
                  className="font-heading text-2xl md:text-3xl tracking-tight break-words"
                  style={{ color: "#0a0a12" }}
                  data-testid="preview-modal-title"
                  title={item?.name || meta.name}
                >
                  {item?.name || meta.name}
                </h2>
                {savedAt && (
                  <div className="text-[11px] text-[#6a6a78] mt-1.5">
                    Mentve: {new Date(savedAt).toLocaleString("hu-HU")}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2" data-html2canvas-ignore="true">
                {isWebinar ? (
                  <>
                    <button
                      onClick={handleCopyMarkdown}
                      disabled={!blueprint}
                      title="Másolás vágólapra (Markdown formátumban)"
                      data-testid="modal-copy-webinar-btn"
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12px] font-medium whitespace-nowrap transition"
                      style={{
                        background: "rgba(10,10,18,0.06)",
                        border: "1px solid rgba(10,10,18,0.10)",
                        color: "#1a1a28",
                      }}
                    >
                      <Copy size={13} weight="duotone" color="#5e50f7" />
                      Másolás
                    </button>
                    <button
                      onClick={handleDownloadMarkdown}
                      disabled={!blueprint}
                      title="Letöltés Markdown formátumban"
                      data-testid="modal-download-webinar-md-btn"
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12px] font-medium whitespace-nowrap transition"
                      style={{
                        background: "rgba(10,10,18,0.06)",
                        border: "1px solid rgba(10,10,18,0.10)",
                        color: "#1a1a28",
                      }}
                    >
                      <DownloadSimple size={13} weight="duotone" color="#5e50f7" />
                      Markdown
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleExportPdf}
                    disabled={exporting || !blueprint}
                    data-testid="modal-export-pdf-btn"
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12px] font-medium whitespace-nowrap transition"
                    style={{
                      background: "rgba(10,10,18,0.06)",
                      border: "1px solid rgba(10,10,18,0.10)",
                      color: "#1a1a28",
                    }}
                  >
                    <FilePdf size={13} weight="duotone" color="#5e50f7" />
                    {exporting ? "..." : "Export PDF"}
                  </button>
                )}
                <button
                  onClick={handleOpenAgent}
                  title="Teljes webinár nézet megnyitása"
                  data-testid="preview-open-agent-btn"
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12px] font-medium whitespace-nowrap transition"
                  style={{
                    background: "rgba(124,108,242,0.12)",
                    border: "1px solid rgba(124,108,242,0.24)",
                    color: "#3a2a4a",
                  }}
                >
                  <ArrowSquareOut size={13} weight="bold" /> Megnyitás
                </button>
                <button
                  onClick={onClose}
                  data-testid="preview-close-btn"
                  className="w-8 h-8 rounded-full flex items-center justify-center transition"
                  style={{ color: "#3a2a4a", background: "rgba(10,10,18,0.04)" }}
                  aria-label="Bezárás"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="relative overflow-y-auto px-8 py-6 flex-1" data-pdf-target="modal">
              {loading ? (
                <div className="text-[13px] text-[#6a6a78] py-10 text-center">
                  Betöltés...
                </div>
              ) : !blueprint || !render ? (
                <div className="text-[13px] text-[#6a6a78] py-10 text-center">
                  Nincs elérhető blueprint.
                </div>
              ) : (
                <div data-testid="blueprint-output">
                  {render(blueprint)}
                </div>
              )}
            </div>

            {/* Footer */}
            <div
              className="relative px-8 py-4 border-t flex items-center gap-2"
              style={{ borderColor: "rgba(10,10,18,0.06)" }}
            >
              <CheckCircle size={14} color="#1d9e75" weight="fill" />
              <span className="text-[11px]" style={{ color: "#0f6e56", fontWeight: 600, letterSpacing: "0.06em" }}>
                TUDÁSTÁR
              </span>
              <span className="text-[11px] text-[#6a6a78]">- {footerLabel}</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
